// src/context/ThemeContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ThemeId = "light" | "dark";

export interface ThemeOption {
  id: ThemeId;
  label: string;
  description: string;
  preview: {
    background: string;
    surface: string;
    accent: string;
  };
}

// Add new entries here when more themes are introduced later —
// everything else (button, drawer, persistence) picks them up automatically.
export const THEME_OPTIONS: ThemeOption[] = [
  {
    id: "light",
    label: "Light",
    description: "Bright surface with our standard colors.",
    preview: { background: "#ffffff", surface: "#ecedf0", accent: "#365d7e" },
  },
  {
    id: "dark",
    label: "Dark",
    description: "Low-light surface, easier on the eyes.",
    preview: { background: "#202124", surface: "#303134", accent: "#8ab4f8" },
  },
];

const STORAGE_KEY = "cnv_theme";
const DEFAULT_THEME: ThemeId = "light";
const VALID_THEME_IDS = THEME_OPTIONS.map((option) => option.id);

function isThemeId(value: unknown): value is ThemeId {
  return typeof value === "string" && VALID_THEME_IDS.includes(value as ThemeId);
}

function getStoredTheme(): ThemeId {
  if (typeof window === "undefined") {
    return DEFAULT_THEME;
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isThemeId(stored) ? stored : DEFAULT_THEME;
  } catch (e) {
    return DEFAULT_THEME;
  }
}

interface ThemeContextValue {
  theme: ThemeId;
  themeClassName: string;
  setTheme: (theme: ThemeId) => void;
  options: ThemeOption[];
  /**
   * Registers a request to force the body-level theme to "light" (e.g. auth
   * pages, which must never render in the user's chosen dark theme). Call
   * once per mounted consumer and invoke the returned function on cleanup —
   * see useForceLightTheme below for the intended usage. Counted, so nested
   * or multiple simultaneous consumers behave correctly.
   */
  requestLightOnly: () => () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(getStoredTheme);
  const [lightOnlyCount, setLightOnlyCount] = useState(0);

  const setTheme = useCallback((next: ThemeId) => {
    setThemeState(isThemeId(next) ? next : DEFAULT_THEME);
  }, []);

  const requestLightOnly = useCallback(() => {
    setLightOnlyCount((count) => count + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setLightOnlyCount((count) => Math.max(0, count - 1));
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      // ignore storage errors (private mode, quota, etc.)
    }
  }, [theme]);

  useEffect(() => {
    // Auth pages (and anything else that asked via requestLightOnly) always
    // win over the user's saved theme — dark mode must never render there.
    const effectiveTheme: ThemeId = lightOnlyCount > 0 ? "light" : theme;

    // Keep <body> in sync too, so full-page backgrounds (outside the
    // React-rendered wrapper) can react to the active theme as well.
    const body = document.body;
    VALID_THEME_IDS.forEach((id) => body.classList.remove(`cnv-theme-${id}`));
    body.classList.add(`cnv-theme-${effectiveTheme}`);
    body.setAttribute("data-cnv-theme", effectiveTheme);
  }, [theme, lightOnlyCount]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      themeClassName: `cnv-theme-${theme}`,
      setTheme,
      options: THEME_OPTIONS,
      requestLightOnly,
    }),
    [theme, setTheme, requestLightOnly],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
}

/**
 * Forces the app-wide theme to "light" for as long as the calling component
 * is mounted, regardless of the user's saved theme preference. Intended for
 * surfaces that must always look the same (auth/sign-in flows).
 */
export function useForceLightTheme(): void {
  const { requestLightOnly } = useTheme();

  useEffect(() => {
    const release = requestLightOnly();
    return release;
  }, [requestLightOnly]);
}
